import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Image as ImageIcon, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLembrancas() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    category: "instituto",
    media_url: "",
    thumbnail_url: "",
    caption: "",
    media_type: "image",
  });
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/lembrancas`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login", { replace: true });
      else setError("Erro ao carregar lembranças.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(me.data);
        await load();
      } catch {
        navigate("/admin/login", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    navigate("/admin/login", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.media_url.trim()) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/admin/lembrancas`, {
        category: form.category,
        media_url: form.media_url.trim(),
        thumbnail_url: form.thumbnail_url.trim() || null,
        caption: form.caption.trim() || null,
        media_type: form.media_type,
      }, { withCredentials: true });
      setForm({ category: form.category, media_url: "", thumbnail_url: "", caption: "", media_type: "image" });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao criar lembrança.");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (it) => {
    await axios.patch(`${API}/admin/lembrancas/${it.id}`, { visible: !it.visible }, { withCredentials: true });
    await load();
  };

  const remove = async (it) => {
    if (!window.confirm(`Remover esta lembrança?`)) return;
    await axios.delete(`${API}/admin/lembrancas/${it.id}`, { withCredentials: true });
    await load();
  };

  const move = async (it, dir) => {
    const sameCat = items.filter((x) => x.category === it.category).sort((a, b) => a.order - b.order);
    const idx = sameCat.findIndex((x) => x.id === it.id);
    const swap = sameCat[idx + (dir === "up" ? -1 : 1)];
    if (!swap) return;
    await axios.post(`${API}/admin/lembrancas/reorder`, {
      items: [{ id: it.id, order: swap.order }, { id: swap.id, order: it.order }],
    }, { withCredentials: true });
    await load();
  };

  const saveCaption = async (it) => {
    await axios.patch(`${API}/admin/lembrancas/${it.id}`, { caption: editCaption }, { withCredentials: true });
    setEditingId(null);
    await load();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-ga-emerald" />
      </div>
    );
  }

  const instituto = items.filter((x) => x.category === "instituto").sort((a, b) => a.order - b.order);
  const enaga = items.filter((x) => x.category === "enaga").sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-lembrancas-page">
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container-ga py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-ga-ink/60 hover:text-ga-ink">← Site</Link>
            <div className="h-4 w-px bg-border" />
            <nav className="flex items-center gap-3 text-sm">
              <Link to="/admin/triagem" className="text-ga-ink/60 hover:text-ga-ink" data-testid="admin-nav-triagem">Triagem</Link>
              <span className="font-display font-black text-ga-ink">Lembranças</span>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
            <span className="text-xs text-ga-ink/70 hidden sm:block">{user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-ga-ink/70 hover:text-ga-ink" data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="container-ga py-8">
        {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>}

        {/* Form */}
        <form onSubmit={submit} className="mb-10 bg-white rounded-2xl border border-border p-5 sm:p-6" data-testid="admin-lembrancas-form">
          <div className="font-display font-bold text-lg text-ga-ink mb-4">Adicionar nova lembrança</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-ga-ink/70 mb-1.5">Categoria</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:border-ga-emerald outline-none"
                data-testid="admin-form-category"
              >
                <option value="instituto">Lembranças do Instituto</option>
                <option value="enaga">Lembranças ENAGA</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-ga-ink/70 mb-1.5">Tipo</label>
              <select
                value={form.media_type}
                onChange={(e) => setForm({ ...form, media_type: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:border-ga-emerald outline-none"
                data-testid="admin-form-type"
              >
                <option value="image">Imagem</option>
                <option value="video">Vídeo</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-ga-ink/70 mb-1.5">URL da mídia *</label>
              <input
                type="url"
                required
                value={form.media_url}
                onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:border-ga-emerald outline-none"
                placeholder="https://... ou /uploads/enaga/foto.jpg"
                data-testid="admin-form-media-url"
              />
              <p className="text-[11px] text-ga-ink/50 mt-1">
                Cole a URL de uma foto antiga do Instagram, ou use caminhos /uploads/... já disponíveis no site.
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-ga-ink/70 mb-1.5">URL thumbnail (opcional, use para vídeos)</label>
              <input
                type="url"
                value={form.thumbnail_url}
                onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:border-ga-emerald outline-none"
                placeholder="https://..."
                data-testid="admin-form-thumb-url"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-ga-ink/70 mb-1.5">Legenda (opcional)</label>
              <textarea
                value={form.caption}
                onChange={(e) => setForm({ ...form, caption: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:border-ga-emerald outline-none min-h-[80px]"
                placeholder="Ex: Festa de encerramento de 2023..."
                data-testid="admin-form-caption"
              />
            </div>
          </div>
          <button type="submit" disabled={saving || !form.media_url.trim()} className="btn-primary mt-5 disabled:opacity-40" data-testid="admin-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Adicionar
          </button>
        </form>

        {/* Lists */}
        {[{ cat: "instituto", label: "Lembranças do Instituto", list: instituto }, { cat: "enaga", label: "Lembranças ENAGA", list: enaga }].map((grp) => (
          <section key={grp.cat} className="mb-10" data-testid={`admin-list-${grp.cat}`}>
            <h2 className="font-display font-bold text-ga-ink text-lg mb-3">{grp.label} <span className="text-sm text-ga-ink/50 font-normal">({grp.list.length})</span></h2>
            {grp.list.length === 0 ? (
              <div className="py-8 text-center text-ga-ink/50 bg-white rounded-2xl border border-dashed border-border">
                Nenhuma lembrança cadastrada.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {grp.list.map((it, idx) => (
                  <article key={it.id} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`admin-lembranca-${it.id}`}>
                    <div className="aspect-video bg-ga-ink/5 relative">
                      <img
                        src={it.thumbnail_url || it.media_url}
                        alt={it.caption || "Lembrança"}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      {!it.visible && (
                        <div className="absolute inset-0 bg-ga-ink/60 flex items-center justify-center">
                          <div className="text-[11px] text-white font-bold uppercase tracking-wider">Oculta</div>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      {editingId === it.id ? (
                        <div className="flex gap-1 mb-2">
                          <input
                            value={editCaption}
                            onChange={(e) => setEditCaption(e.target.value)}
                            className="flex-1 text-xs px-2 py-1.5 rounded-md border border-border outline-none"
                            data-testid={`admin-edit-caption-${it.id}`}
                          />
                          <button onClick={() => saveCaption(it)} className="p-1.5 rounded-md bg-ga-emerald text-white" data-testid={`admin-save-caption-${it.id}`}><Check className="w-3 h-3" /></button>
                          <button onClick={() => setEditingId(null)} className="p-1.5 rounded-md bg-border"><X className="w-3 h-3" /></button>
                        </div>
                      ) : (
                        <div
                          className="text-xs text-ga-ink/70 mb-2 cursor-pointer hover:text-ga-ink min-h-[2.5em] line-clamp-2"
                          onClick={() => { setEditingId(it.id); setEditCaption(it.caption || ""); }}
                          title="Clique para editar"
                        >
                          {it.caption || <span className="italic text-ga-ink/40">Clique para adicionar legenda</span>}
                        </div>
                      )}
                      <div className="flex items-center gap-1 flex-wrap">
                        <button onClick={() => move(it, "up")} disabled={idx === 0} className="p-1.5 rounded-md bg-background hover:bg-ga-ink/5 disabled:opacity-30" title="Subir" data-testid={`admin-move-up-${it.id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => move(it, "down")} disabled={idx === grp.list.length - 1} className="p-1.5 rounded-md bg-background hover:bg-ga-ink/5 disabled:opacity-30" title="Descer" data-testid={`admin-move-down-${it.id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                        <button onClick={() => toggleVisible(it)} className="p-1.5 rounded-md bg-background hover:bg-ga-ink/5" title={it.visible ? "Ocultar" : "Mostrar"} data-testid={`admin-toggle-${it.id}`}>
                          {it.visible ? <Eye className="w-3.5 h-3.5 text-ga-emerald" /> : <EyeOff className="w-3.5 h-3.5 text-ga-ink/40" />}
                        </button>
                        <div className="ml-auto" />
                        <button onClick={() => remove(it)} className="p-1.5 rounded-md bg-red-50 hover:bg-red-100 text-red-600" title="Remover" data-testid={`admin-remove-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        ))}
      </main>
    </div>
  );
}
